import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiSearch, FiClock, FiStar, FiChevronRight, FiSettings, FiHelpCircle, FiPlay, FiRepeat, FiCheck, FiRuler, FiBox, FiHardDrive, FiCpu, FiZap, FiActivity, FiDroplet, FiGrid, FiDatabase, FiBattery, FiTrendingUp, FiWifi, FiHash, FiGlobe, FiX, FiArrowRight } from 'react-icons/fi';
import { conversionData } from '../../utils/unitConversions/conversionData';
import UnitConversionCalculator from './UnitConversionCalculator';
import ConversionSearch from './ConversionSearch';
import ConversionHistory from './ConversionHistory';
import ConversionsList from './ConversionsList';
import CategoryButton from './CategoryButton';
import { useDarkMode } from '../../context/DarkModeContext';
import { conversionCategories } from '../../utils/conversionCategories';
import { getUnitConversionSEO, generateFAQStructuredData, generateHowToStructuredData } from '../../utils/unitConversionSEO';

const generateConversionPairs = (units) => {
  const pairs = [];
  for (let i = 0; i < units.length; i++) {
    for (let j = 0; j < units.length; j++) {
      if (i !== j) {
        pairs.push({
          from: units[i],
          to: units[j],
          label: `${units[i]} to ${units[j]}`
        });
      }
    }
  }
  return pairs;
};

const categoryThemes = {
  length: {
    primary: 'text-indigo-700',
    secondary: 'text-indigo-600/90',
    accent: 'text-indigo-500',
    muted: 'text-indigo-600/70',
    bg: {
      light: 'bg-gradient-to-br from-indigo-50/30 via-indigo-50/20 to-transparent',
      lighter: 'bg-gradient-to-r from-indigo-50/20 to-transparent',
      hover: 'hover:bg-gradient-to-r hover:from-indigo-50/40 hover:via-indigo-50/20 hover:to-transparent',
      active: 'bg-gradient-to-br from-indigo-100/50 via-indigo-50/30 to-white',
      selected: 'bg-indigo-50/40',
      focused: 'bg-gradient-to-r from-indigo-50/50 via-indigo-50/30 to-indigo-50/10'
    },
    icon: {
      bg: 'bg-gradient-to-br from-indigo-100/30 via-indigo-50/30 to-indigo-50/10',
      text: 'text-indigo-600/90',
      hover: 'group-hover:text-indigo-700 group-hover:bg-indigo-100/40',
      active: 'text-indigo-800 bg-indigo-100/60',
      disabled: 'text-indigo-400 bg-indigo-50/30'
    },
    border: {
      default: 'border-indigo-100/60',
      hover: 'hover:border-indigo-200/70',
      active: 'border-indigo-200/80',
      focus: 'focus:border-indigo-300/70',
      selected: 'border-indigo-300/60'
    },
    shadow: {
      hover: 'hover:shadow-[0_2px_8px_-2px_rgba(99,102,241,0.12)]',
      active: 'shadow-[inset_0_1px_3px_-1px_rgba(99,102,241,0.12)]',
      focus: 'shadow-[0_0_0_3px_rgba(99,102,241,0.08)]'
    },
    ring: {
      focus: 'focus:ring-2 focus:ring-indigo-200/50',
      selected: 'ring-1 ring-indigo-200/40'
    },
    state: {
      loading: {
        bg: 'bg-gradient-to-r from-indigo-50/40 via-indigo-50/20 to-indigo-50/40',
        text: 'text-indigo-600/70',
        border: 'border-indigo-100/50'
      },
      disabled: {
        bg: 'bg-indigo-50/20',
        text: 'text-indigo-400',
        border: 'border-indigo-100/30'
      },
      error: {
        bg: 'bg-red-50/30',
        text: 'text-red-600',
        border: 'border-red-200/50'
      },
      success: {
        bg: 'bg-green-50/30',
        text: 'text-green-600',
        border: 'border-green-200/50'
      }
    },
    animation: {
      hover: 'transition-all duration-200 ease-out',
      active: 'transition-all duration-150 ease-in-out',
      expand: 'transition-all duration-300 ease-in-out'
    }
  },
  weight: {
    primary: 'text-emerald-700',
    secondary: 'text-emerald-600/90',
    accent: 'text-emerald-500',
    muted: 'text-emerald-600/70',
    bg: {
      light: 'bg-gradient-to-br from-emerald-50/30 via-emerald-50/20 to-transparent',
      lighter: 'bg-gradient-to-r from-emerald-50/20 to-transparent',
      hover: 'hover:bg-gradient-to-r hover:from-emerald-50/40 hover:via-emerald-50/20 hover:to-transparent',
      active: 'bg-gradient-to-br from-emerald-100/50 via-emerald-50/30 to-white',
      selected: 'bg-emerald-50/40',
      focused: 'bg-gradient-to-r from-emerald-50/50 via-emerald-50/30 to-emerald-50/10'
    },
    icon: {
      bg: 'bg-gradient-to-br from-emerald-100/30 via-emerald-50/30 to-emerald-50/10',
      text: 'text-emerald-600/90',
      hover: 'group-hover:text-emerald-700 group-hover:bg-emerald-100/40',
      active: 'text-emerald-800 bg-emerald-100/60',
      disabled: 'text-emerald-400 bg-emerald-50/30'
    },
    border: {
      default: 'border-emerald-100/60',
      hover: 'hover:border-emerald-200/70',
      active: 'border-emerald-200/80',
      focus: 'focus:border-emerald-300/70',
      selected: 'border-emerald-300/60'
    },
    shadow: {
      hover: 'hover:shadow-[0_2px_8px_-2px_rgba(52,211,153,0.12)]',
      active: 'shadow-[inset_0_1px_3px_-1px_rgba(52,211,153,0.12)]',
      focus: 'shadow-[0_0_0_3px_rgba(52,211,153,0.08)]'
    },
    ring: {
      focus: 'focus:ring-2 focus:ring-emerald-200/50',
      selected: 'ring-1 ring-emerald-200/40'
    },
    state: {
      loading: {
        bg: 'bg-gradient-to-r from-emerald-50/40 via-emerald-50/20 to-emerald-50/40',
        text: 'text-emerald-600/70',
        border: 'border-emerald-100/50'
      },
      disabled: {
        bg: 'bg-emerald-50/20',
        text: 'text-emerald-400',
        border: 'border-emerald-100/30'
      },
      error: {
        bg: 'bg-red-50/30',
        text: 'text-red-600',
        border: 'border-red-200/50'
      },
      success: {
        bg: 'bg-green-50/30',
        text: 'text-green-600',
        border: 'border-green-200/50'
      }
    },
    animation: {
      hover: 'transition-all duration-200 ease-out',
      active: 'transition-all duration-150 ease-in-out',
      expand: 'transition-all duration-300 ease-in-out'
    }
  },
  temperature: {
    primary: 'text-rose-700',
    secondary: 'text-rose-600/90',
    accent: 'text-rose-500',
    muted: 'text-rose-600/70',
    bg: {
      light: 'bg-gradient-to-br from-rose-50/30 via-rose-50/20 to-transparent',
      lighter: 'bg-gradient-to-r from-rose-50/20 to-transparent',
      hover: 'hover:bg-gradient-to-r hover:from-rose-50/40 hover:via-rose-50/20 hover:to-transparent',
      active: 'bg-gradient-to-br from-rose-100/50 via-rose-50/30 to-white',
      selected: 'bg-rose-50/40',
      focused: 'bg-gradient-to-r from-rose-50/50 via-rose-50/30 to-rose-50/10'
    },
    icon: {
      bg: 'bg-gradient-to-br from-rose-100/30 via-rose-50/30 to-rose-50/10',
      text: 'text-rose-600/90',
      hover: 'group-hover:text-rose-700 group-hover:bg-rose-100/40',
      active: 'text-rose-800 bg-rose-100/60',
      disabled: 'text-rose-400 bg-rose-50/30'
    },
    border: {
      default: 'border-rose-100/60',
      hover: 'hover:border-rose-200/70',
      active: 'border-rose-200/80',
      focus: 'focus:border-rose-300/70',
      selected: 'border-rose-300/60'
    },
    shadow: {
      hover: 'hover:shadow-[0_2px_8px_-2px_rgba(244,63,94,0.12)]',
      active: 'shadow-[inset_0_1px_3px_-1px_rgba(244,63,94,0.12)]',
      focus: 'shadow-[0_0_0_3px_rgba(244,63,94,0.08)]'
    },
    ring: {
      focus: 'focus:ring-2 focus:ring-rose-200/50',
      selected: 'ring-1 ring-rose-200/40'
    },
    state: {
      loading: {
        bg: 'bg-gradient-to-r from-rose-50/40 via-rose-50/20 to-rose-50/40',
        text: 'text-rose-600/70',
        border: 'border-rose-100/50'
      },
      disabled: {
        bg: 'bg-rose-50/20',
        text: 'text-rose-400',
        border: 'border-rose-100/30'
      },
      error: {
        bg: 'bg-red-50/30',
        text: 'text-red-600',
        border: 'border-red-200/50'
      },
      success: {
        bg: 'bg-green-50/30',
        text: 'text-green-600',
        border: 'border-green-200/50'
      }
    },
    animation: {
      hover: 'transition-all duration-200 ease-out',
      active: 'transition-all duration-150 ease-in-out',
      expand: 'transition-all duration-300 ease-in-out'
    }
  },
  volume: {
    primary: 'text-cyan-700',
    secondary: 'text-cyan-600/90',
    accent: 'text-cyan-500',
    muted: 'text-cyan-600/70',
    bg: {
      light: 'bg-gradient-to-br from-cyan-50/30 via-cyan-50/20 to-transparent',
      lighter: 'bg-gradient-to-r from-cyan-50/20 to-transparent',
      hover: 'hover:bg-gradient-to-r hover:from-cyan-50/40 hover:via-cyan-50/20 hover:to-transparent',
      active: 'bg-gradient-to-br from-cyan-100/50 via-cyan-50/30 to-white',
      selected: 'bg-cyan-50/40',
      focused: 'bg-gradient-to-r from-cyan-50/50 via-cyan-50/30 to-cyan-50/10'
    },
    icon: {
      bg: 'bg-gradient-to-br from-cyan-100/30 via-cyan-50/30 to-cyan-50/10',
      text: 'text-cyan-600/90',
      hover: 'group-hover:text-cyan-700 group-hover:bg-cyan-100/40',
      active: 'text-cyan-800 bg-cyan-100/60',
      disabled: 'text-cyan-400 bg-cyan-50/30'
    },
    border: {
      default: 'border-cyan-100/60',
      hover: 'hover:border-cyan-200/70',
      active: 'border-cyan-200/80',
      focus: 'focus:border-cyan-300/70',
      selected: 'border-cyan-300/60'
    },
    shadow: {
      hover: 'hover:shadow-[0_2px_8px_-2px_rgba(34,211,238,0.12)]',
      active: 'shadow-[inset_0_1px_3px_-1px_rgba(34,211,238,0.12)]',
      focus: 'shadow-[0_0_0_3px_rgba(34,211,238,0.08)]'
    },
    ring: {
      focus: 'focus:ring-2 focus:ring-cyan-200/50',
      selected: 'ring-1 ring-cyan-200/40'
    },
    state: {
      loading: {
        bg: 'bg-gradient-to-r from-cyan-50/40 via-cyan-50/20 to-cyan-50/40',
        text: 'text-cyan-600/70',
        border: 'border-cyan-100/50'
      },
      disabled: {
        bg: 'bg-cyan-50/20',
        text: 'text-cyan-400',
        border: 'border-cyan-100/30'
      },
      error: {
        bg: 'bg-red-50/30',
        text: 'text-red-600',
        border: 'border-red-200/50'
      },
      success: {
        bg: 'bg-green-50/30',
        text: 'text-green-600',
        border: 'border-green-200/50'
      }
    },
    animation: {
      hover: 'transition-all duration-200 ease-out',
      active: 'transition-all duration-150 ease-in-out',
      expand: 'transition-all duration-300 ease-in-out'
    }
  },
  speed: {
    primary: 'text-violet-700',
    secondary: 'text-violet-600/90',
    accent: 'text-violet-500',
    muted: 'text-violet-600/70',
    bg: {
      light: 'bg-gradient-to-br from-violet-50/30 via-violet-50/20 to-transparent',
      lighter: 'bg-gradient-to-r from-violet-50/20 to-transparent',
      hover: 'hover:bg-gradient-to-r hover:from-violet-50/40 hover:via-violet-50/20 hover:to-transparent',
      active: 'bg-gradient-to-br from-violet-100/50 via-violet-50/30 to-white',
      selected: 'bg-violet-50/40',
      focused: 'bg-gradient-to-r from-violet-50/50 via-violet-50/30 to-violet-50/10'
    },
    icon: {
      bg: 'bg-gradient-to-br from-violet-100/30 via-violet-50/30 to-violet-50/10',
      text: 'text-violet-600/90',
      hover: 'group-hover:text-violet-700 group-hover:bg-violet-100/40',
      active: 'text-violet-800 bg-violet-100/60',
      disabled: 'text-violet-400 bg-violet-50/30'
    },
    border: {
      default: 'border-violet-100/60',
      hover: 'hover:border-violet-200/70',
      active: 'border-violet-200/80',
      focus: 'focus:border-violet-300/70',
      selected: 'border-violet-300/60'
    },
    shadow: {
      hover: 'hover:shadow-[0_2px_8px_-2px_rgba(139,92,246,0.12)]',
      active: 'shadow-[inset_0_1px_3px_-1px_rgba(139,92,246,0.12)]',
      focus: 'shadow-[0_0_0_3px_rgba(139,92,246,0.08)]'
    },
    ring: {
      focus: 'focus:ring-2 focus:ring-violet-200/50',
      selected: 'ring-1 ring-violet-200/40'
    },
    state: {
      loading: {
        bg: 'bg-gradient-to-r from-violet-50/40 via-violet-50/20 to-violet-50/40',
        text: 'text-violet-600/70',
        border: 'border-violet-100/50'
      },
      disabled: {
        bg: 'bg-violet-50/20',
        text: 'text-violet-400',
        border: 'border-violet-100/30'
      },
      error: {
        bg: 'bg-red-50/30',
        text: 'text-red-600',
        border: 'border-red-200/50'
      },
      success: {
        bg: 'bg-green-50/30',
        text: 'text-green-600',
        border: 'border-green-200/50'
      }
    },
    animation: {
      hover: 'transition-all duration-200 ease-out',
      active: 'transition-all duration-150 ease-in-out',
      expand: 'transition-all duration-300 ease-in-out'
    }
  }
};

// Helper function to generate hover effects
const generateHoverEffect = (theme) => `
  ${theme.animation.hover}
  ${theme.bg.hover}
  ${theme.border.hover}
  ${theme.shadow.hover}
`;

const CategorySection = ({ category, isSelected, onSelect, selectedUnits, onUnitSelect }) => {
  const [isExpanded, setIsExpanded] = useState(isSelected);
  const [showAllConversions, setShowAllConversions] = useState(false);

  // Add default theme for undefined categories
  const defaultTheme = {
    primary: 'text-gray-700',
    secondary: 'text-gray-600',
    bg: {
      light: 'bg-gray-50/40',
      hover: 'hover:bg-gray-50/60',
      active: 'bg-gray-100/50'
    },
    border: 'border-gray-200',
    icon: {
      bg: 'bg-gray-100/30',
      text: 'text-gray-700'
    },
    gradient: 'from-gray-50/50 to-white'
  };

  // Safely get theme with fallback
  const theme = categoryThemes[category.path] || defaultTheme;

  useEffect(() => {
    setIsExpanded(isSelected);
  }, [isSelected]);

  const handleClick = () => {
    setIsExpanded(!isExpanded);
    onSelect(category.path);
  };

  const handleConversionPairClick = (pair) => {
    // First, ensure the category is selected
    if (!isSelected) {
      onSelect(category.path);
      setIsExpanded(true);
    }
    
    // Then set the conversion units without triggering conversion
    onUnitSelect({
      category: category.path,
      from: pair.from,
      to: pair.to
    });
  };

  return (
    <div className="px-1.5 py-0.5">
      {/* Main category button with enhanced hover and focus */}
      <button
        onClick={handleClick}
        className={`w-full rounded-lg transition-all duration-300 group outline-none
          focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950
          shadow-sm
          ${isExpanded 
            ? `${theme.bg.active} shadow-md scale-105` 
            : 'hover:bg-primary-50 dark:hover:bg-primary-900/20'}
        `}
        tabIndex={0}
        aria-pressed={isExpanded}
        role="button"
      >
        <div className="px-3 py-2">
          <div className="flex items-center gap-2.5">
            {/* Enhanced icon container with better hover */}
            <div className={`w-7 h-7 flex items-center justify-center
                         rounded-md ${theme.icon.bg}
                         transition-all duration-300 ease-out
                         group-hover:scale-105 group-hover:rotate-3
                         ${isExpanded ? theme.icon.text : 'text-gray-500'}`}>
              <span className="text-sm">{getCategoryIcon(category.path)}</span>
            </div>
            {/* Refined text container with better spacing */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span className={`font-medium text-[13px] tracking-tight truncate
                              transition-colors duration-200
                              ${isExpanded ? theme.primary : 'text-gray-700 dark:text-neutral-200'}`}>
                  {category.name}
                </span>
                <FiChevronRight className={`w-3.5 h-3.5 flex-shrink-0
                                       transition-all duration-300
                                       ${isExpanded 
                                         ? `${theme.secondary} rotate-90` 
                                         : 'text-gray-400 group-hover:translate-x-0.5'}`} />
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[11px] text-gray-400 dark:text-neutral-400">
                  {category.conversionPairs.length} conversions
                </span>
              </div>
            </div>
          </div>
        </div>
      </button>
      {/* Subcategories with refined spacing and hover */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2 }}
            className="pl-9 pr-1.5 py-1.5"
          >
            <div className="relative">
              {/* Refined left border line */}
              <div className="absolute left-0 top-1 bottom-1 w-px 
                           bg-gradient-to-b from-transparent 
                           via-primary-200/40 dark:via-primary-900/40 to-transparent" />
              {/* Conversion list with better spacing */}
              <div className="space-y-0.5 max-h-[220px] overflow-y-auto
                           scrollbar-thin scrollbar-thumb-primary-100 dark:scrollbar-thumb-primary-900/40
                           scrollbar-track-transparent pr-1">
                {(showAllConversions ? category.conversionPairs : category.conversionPairs.slice(0, 5))
                  .map((pair, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleConversionPairClick(pair)}
                    className={`w-full px-2.5 py-1.5 rounded-md text-[11px]
                      transition-all duration-300 group relative outline-none
                      focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950
                      active:scale-98
                      ${selectedUnits.from === pair.from && selectedUnits.to === pair.to
                        ? `${theme.bg.active} ${theme.primary} shadow-md`
                        : 'hover:bg-primary-50 dark:hover:bg-primary-900/20 text-gray-700 dark:text-neutral-100'}
                    `}
                    tabIndex={0}
                    aria-pressed={selectedUnits.from === pair.from && selectedUnits.to === pair.to}
                    role="option"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium truncate max-w-[45%]">{pair.from}</span>
                      <span className={`mx-1.5 flex-shrink-0 transition-all duration-300
                                   ${theme.secondary} opacity-50 group-hover:opacity-100
                                   group-hover:scale-110`}>
                        <svg className="w-2.5 h-2.5 transform -rotate-45" viewBox="0 0 24 24" 
                             fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M5 12h14m-7-7l7 7-7 7" />
                        </svg>
                      </span>
                      <span className="truncate max-w-[45%]">{pair.to}</span>
                    </div>
                  </button>
                ))}
                {/* Show more button with refined styling */}
                {category.conversionPairs.length > 5 && (
                  <div className="pt-1">
                    <button
                      onClick={() => setShowAllConversions(!showAllConversions)}
                      className={`w-full px-2.5 py-1.5 text-[11px] rounded-md
                        transition-all duration-300 group relative outline-none
                        focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950
                        hover:bg-primary-50 dark:hover:bg-primary-900/20 text-primary-700 dark:text-primary-300`}
                      tabIndex={0}
                      aria-pressed={showAllConversions}
                      role="button"
                    >
                      <div className="flex items-center justify-center gap-1">
                        <span className={`font-medium ${theme.secondary}`}>
                          {showAllConversions 
                            ? 'Show less' 
                            : `${category.conversionPairs.length - 5} more`}
                        </span>
                        <motion.span
                          animate={{ rotate: showAllConversions ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                          className={`${theme.secondary} opacity-50 
                                  group-hover:opacity-100 transition-opacity`}
                        >
                          <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" 
                               stroke="currentColor" strokeWidth="2">
                            <path d="M19 9l-7 7-7-7" />
                          </svg>
                        </motion.span>
                      </div>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Helper function to get category icons
const getCategoryIcon = (categoryPath) => {
  const icons = {
    // Common Units
    length: "📏",
    weight: "⚖️",
    temperature: "🌡️",
    time: "⏱️",
    volume: "🧪",
    area: "📐",
    
    // Scientific & Engineering
    power: "⚡",              // Lightning bolt for power
    voltage: "🔌",            // Electric plug for voltage
    energy: "🔋",             // Battery for energy
    resistance: "Ω",          // Omega symbol for resistance
    frequency: "〰️",          // Wave symbol for frequency
    'electric-charge': "⚛️",  // Atom for electric charge
    current: "↯",            // Special arrow for current
    capacitance: "🧲",        // Magnet for capacitance
    inductance: "🌀",         // Spiral for inductance
    
    // Digital & Computing
    number: "🔢",
    color: "🎨",
    image: "🖼️",
    data: "💾",
    
    // Additional Categories
    pressure: "📊",
    force: "💪",
    angle: "📐",
    velocity: "🏃",
    acceleration: "🚀",
    density: "💧",
    flow: "🌊",
    torque: "🔄",
    luminosity: "💡",
    sound: "🔊",
    magnetic: "🧲"
  };

  // Return the specific icon or a default one if not found
  return icons[categoryPath] || "🔄";
};

// Helper function to get popular conversions (you can customize this)
const getPopularConversions = (pairs) => {
  // Return first 3 pairs for now - you can customize this logic
  return pairs.slice(0, 3);
};

const popularConversions = [
    { id: 1, category: 'length', label: 'Meters to Feet', icon: '📏' },
    { id: 2, category: 'weight', label: 'Kilos to Pounds', icon: '⚖️' },
    { id: 3, category: 'temperature', label: 'Celsius to Fahrenheit', icon: '🌡️' }
  ];
// Add this new component for the landing state
const LandingState = ({ onSelectConversion }) => {
  return (
    <div className="space-y-10">
      {/* Hero Section - More refined messaging */}
      <div className="bg-gradient-to-br from-white via-gray-50/50 to-white
                    rounded-2xl border border-gray-200/80 p-12
                    shadow-sm relative overflow-hidden">
        <div className="relative z-10 max-w-3xl">
          <h1 className="text-[32px] font-semibold text-gray-900 mb-4
                      tracking-tight leading-tight">
            Precision Unit Conversion, 
            <span className="text-blue-600/90">Simplified</span>
          </h1>
          <p className="text-gray-600 text-lg leading-relaxed
                     tracking-tight">
            From everyday measurements to scientific calculations, convert units 
            with confidence. Built for precision, designed for clarity, and 
            optimized for your workflow.
          </p>
          
          {/* Quick Action Buttons */}
          <div className="flex items-center gap-4 mt-8">
            <button className="px-5 py-2.5 bg-blue-600 text-white
                           rounded-lg text-[14px] font-medium
                           hover:bg-blue-700 transition-colors
                           tracking-tight shadow-sm">
              Start Converting
            </button>
            <button className="px-5 py-2.5 text-gray-600
                           rounded-lg text-[14px] font-medium
                           hover:text-gray-900 transition-colors
                           tracking-tight flex items-center gap-2">
              <FiPlay className="w-4 h-4" />
              How it works
            </button>
          </div>
        </div>
        
        {/* Enhanced decorative elements */}
        <div className="absolute inset-0 bg-grid-gray-900/[0.02] -z-0" />
        <div className="absolute -right-12 -bottom-12 w-64 h-64
                     bg-blue-50/30 rounded-full blur-3xl" />
      </div>

      {/* Features Grid - Refined messaging */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl p-7 border border-gray-200/80
                     shadow-sm hover:shadow-md transition-all duration-300">
          <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center
                       justify-center mb-5">
            <svg className="w-6 h-6 text-blue-500" fill="none" viewBox="0 0 24 24"
                 stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-gray-900 font-medium text-[15px] mb-2.5 tracking-tight">
            Scientific Precision
          </h3>
          <p className="text-gray-600 text-[13px] leading-relaxed">
            Advanced conversion engine with support for complex unit systems. 
            Maintain accuracy across all calculations with intelligent rounding.
          </p>
        </div>

        {/* Additional features with refined content */}
        <div className="bg-white rounded-xl p-7 border border-gray-200/80
                     shadow-sm hover:shadow-md transition-all duration-300">
          <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center
                       justify-center mb-5">
            <svg className="w-6 h-6 text-emerald-500" fill="none" viewBox="0 0 24 24"
                 stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h3 className="text-gray-900 font-medium text-[15px] mb-2.5 tracking-tight">
            Real-time Conversion
          </h3>
          <p className="text-gray-600 text-[13px] leading-relaxed">
            Instant results with live updates. Smart formatting adjusts 
            automatically for readability and scientific notation.
          </p>
        </div>

        <div className="bg-white rounded-xl p-7 border border-gray-200/80
                     shadow-sm hover:shadow-md transition-all duration-300">
          <div className="w-12 h-12 rounded-xl bg-violet-50 flex items-center
                       justify-center mb-5">
            <svg className="w-6 h-6 text-violet-500" fill="none" viewBox="0 0 24 24"
                 stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
          </div>
          <h3 className="text-gray-900 font-medium text-[15px] mb-2.5 tracking-tight">
            Smart Organization
          </h3>
          <p className="text-gray-600 text-[13px] leading-relaxed">
            Intelligently organized categories with contextual suggestions. 
            Find any conversion with our powerful search system.
          </p>
        </div>
      </div>

      {/* Quick Access Section - Enhanced */}
      <div className="bg-white rounded-xl border border-gray-200/80 p-8
                    shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-[15px] font-medium text-gray-900 mb-1">
              Common Conversions
            </h3>
            <p className="text-[13px] text-gray-500">
              Quick access to frequently used conversions
            </p>
          </div>
          <button className="text-sm text-blue-600 hover:text-blue-700
                         font-medium tracking-tight transition-colors">
            Browse All
          </button>
        </div>
        
        {/* Enhanced conversion buttons */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {popularConversions.map((conversion) => (
            <button
              key={conversion.id}
              onClick={() => onSelectConversion(conversion)}
              className="flex items-center gap-3 p-3.5 rounded-lg
                     border border-gray-200/60 bg-white
                     hover:border-gray-300/70 hover:shadow-sm
                     transition-all duration-200 group"
            >
              <span className={`${categoryThemes[conversion.category].icon.text}
                            text-lg group-hover:scale-110 transition-transform`}>
                {conversion.icon}
              </span>
              <span className="text-[13px] text-gray-700 tracking-tight
                           group-hover:text-gray-900 transition-colors">
                {conversion.label}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// First, let's fix the common conversions array
const commonConversions = [
  {
    from: 'Decimal',
    to: 'Binary',
    category: 'number',
    label: 'DEC → BIN'
  },
  {
    from: 'Meters',
    to: 'Feet',
    category: 'length',
    label: 'M → FT'
  },
  {
    from: 'Celsius',
    to: 'Fahrenheit',
    category: 'temperature',
    label: '°C → °F'
  },
  {
    from: 'Kilograms',
    to: 'Pounds',
    category: 'weight',
    label: 'KG → LB'
  }
];

// Then fix the common conversions section in the Sidebar
const Sidebar = ({ categories, selectedCategory, selectedUnits, onCategorySelect, onUnitSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchRef = useRef(null);
  const { darkMode } = useDarkMode();

  const handleCommonConversionClick = (conversion) => {
    onCategorySelect(conversion.category);
    onUnitSelect({
      from: conversion.from,
      to: conversion.to,
      category: conversion.category
    });
  };

  useEffect(() => {
    if (searchTerm.length >= 2) {
      const results = [];
      categories.forEach(category => {
        category.conversionPairs.forEach(pair => {
          if (
            pair.from.toLowerCase().includes(searchTerm.toLowerCase()) ||
            pair.to.toLowerCase().includes(searchTerm.toLowerCase()) ||
            category.name.toLowerCase().includes(searchTerm.toLowerCase())
          ) {
            results.push({
              category: category.path,
              categoryName: category.name,
              from: pair.from,
              to: pair.to,
              label: `${pair.from} to ${pair.to}`
            });
          }
        });
      });
      setSearchResults(results.slice(0, 8)); // Limit to 8 results
      setIsSearching(true);
    } else {
      setSearchResults([]);
      setIsSearching(false);
    }
  }, [searchTerm, categories]);

  const renderSearchSection = () => (
    <div className="relative group" ref={searchRef}>
      <input
        type="text"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Search conversions..."
        className="w-full pl-9 pr-12 py-2 
                  bg-gray-50/60 border border-gray-200/70 
                  rounded-lg text-[12px] text-gray-700
                  placeholder:text-[11px] placeholder:text-gray-400
                  tracking-tight leading-relaxed
                  transition-all duration-200
                  focus:bg-white focus:border-blue-200/80
                  focus:ring-2 focus:ring-blue-50/40
                  hover:border-gray-300/60
                  outline-none"
      />
      <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2
                          w-3.5 h-3.5 text-gray-400/80" />
      {searchTerm && (
        <button
          onClick={() => setSearchTerm('')}
          className="absolute right-8 top-1/2 -translate-y-1/2
                    p-1 rounded-full hover:bg-gray-100
                    text-gray-400 hover:text-gray-600
                    transition-colors"
        >
          <FiX className="w-3.5 h-3.5" />
        </button>
      )}
      <kbd className="absolute right-2.5 top-1/2 -translate-y-1/2
                     hidden lg:inline-flex items-center gap-0.5
                     px-1.5 h-4 text-[9px] font-medium
                     bg-gray-100/80 text-gray-500/90
                     rounded border border-gray-200/60">
        ⌘K
      </kbd>

      {/* Search Results Dropdown - Improved styling */}
      <AnimatePresence>
        {isSearching && searchResults.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="absolute w-full mt-1.5 bg-white rounded-lg 
                      shadow-lg border border-gray-200/80 
                      overflow-hidden z-50"
          >
            {searchResults.map((result, index) => (
              <button
                key={index}
                className="w-full px-3 py-2 text-left 
                          hover:bg-gray-50/80 transition-colors
                          flex flex-col gap-0.5 
                          border-b border-gray-100/60 last:border-0"
                onClick={() => {
                  onCategorySelect(result.category);
                  onUnitSelect({
                    category: result.category,
                    from: result.from,
                    to: result.to
                  });
                  setSearchTerm('');
                  setIsSearching(false);
                }}
              >
                <div className="flex items-center gap-2 text-[12px] text-gray-700">
                  <span className="font-medium">{result.from}</span>
                  <FiArrowRight className="w-3 h-3 text-gray-400/80" />
                  <span>{result.to}</span>
                </div>
                <span className="text-[10px] text-gray-500">{result.categoryName}</span>
              </button>
            ))}
          </motion.div>
        )}

        {isSearching && searchTerm.length >= 2 && searchResults.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="absolute w-full mt-1.5 bg-white rounded-lg 
                      shadow-lg border border-gray-200/80 
                      p-3 z-50"
          >
            <div className="flex flex-col items-center gap-1.5">
              <span className="text-[12px] text-gray-600">
                No results found for "{searchTerm}"
              </span>
              <span className="text-[11px] text-gray-400">
                Try different keywords or browse categories
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  return (
    <div
      tabIndex={-1}
      className={`w-[340px] rounded-2xl border shadow-lg shadow-primary-100/20 dark:shadow-primary-900/20 backdrop-blur-sm sticky top-[84px] overflow-hidden
        ${darkMode ? 'bg-gradient-to-br from-gray-950/95 via-gray-900/95 to-gray-950/98 border-gray-800 text-neutral-100' : 'bg-gradient-to-br from-white/95 to-gray-50/90 border-gray-200/80 text-gray-800'}
        focus-within:ring-2 focus-within:ring-primary-400 focus-within:ring-offset-0
        focus-within:border-transparent
      `}
    >
      {/* Header Section */}
      <div className={`px-6 pt-7 pb-5 border-b ${darkMode ? 'border-gray-800 bg-gray-950/90' : 'border-gray-100/80'}`}>
        <div className="flex items-center justify-between mb-5">
          <h2 className={`text-[17px] font-semibold tracking-tight ${darkMode ? 'text-neutral-100' : 'text-gray-900'}`}>Categories</h2>
          <button className={`p-2 rounded-md transition-all duration-200 outline-none
            focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950
            shadow-sm
            ${darkMode ? 'text-gray-500 hover:text-primary-400 hover:bg-gray-900/60' : 'text-gray-400 hover:text-primary-500 hover:bg-primary-50/60'}`}
            aria-label="Sidebar settings"
            tabIndex={0}
          >
            <FiSettings className="w-[16px] h-[16px]" />
          </button>
        </div>
        <ConversionSearch 
          onSelect={({ category, fromUnit, toUnit }) => {
            onCategorySelect(category);
            onUnitSelect({
              category,
              from: fromUnit,
              to: toUnit
            });
          }}
          darkMode={darkMode}
        />
      </div>
      {/* Common Conversions Section - Refined edges */}
      <div className={`px-6 py-4 border-b ${darkMode ? 'bg-gradient-to-r from-gray-950/80 via-gray-900/80 to-gray-950/90 border-gray-800/60' : 'bg-gradient-to-b from-gray-50/50 to-transparent border-gray-100/60'}`}>
        <div className="mb-3">
          <span className={`text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-neutral-400' : 'text-gray-700'}`}>Common Conversions</span>
        </div>
        <div className="space-y-1.5">
          {commonConversions.map((conversion, idx) => (
            <button
              key={idx}
              onClick={() => handleCommonConversionClick(conversion)}
              className={`w-full px-3 py-2 text-sm text-left rounded-lg border transition-all duration-200 group outline-none
                shadow-sm
                ${darkMode
                  ? `bg-gray-900/80 border-gray-800/60 text-neutral-100 hover:bg-primary-900/20 hover:border-primary-500`
                  : `bg-white border-gray-200/60 text-gray-800 hover:border-primary-300/80 hover:bg-primary-50/80 hover:text-primary-600`}
                ${selectedUnits.from === conversion.from && selectedUnits.to === conversion.to && selectedCategory === conversion.category
                  ? (darkMode ? 'border-primary-500 bg-primary-900/20 text-primary-400 shadow-md' : 'border-primary-300 bg-primary-50/50 text-primary-700 shadow-md')
                           : ''}`}
              tabIndex={0}
              aria-pressed={selectedUnits.from === conversion.from && selectedUnits.to === conversion.to && selectedCategory === conversion.category}
              role="option"
            >
              <div className="flex items-center justify-between">
                <span className="font-medium">{conversion.from}</span>
                <span className={`${darkMode ? 'text-neutral-400 group-hover:text-primary-400' : 'text-gray-500 group-hover:text-primary-600'}`}>→</span>
                <span className="font-medium">{conversion.to}</span>
              </div>
            </button>
          ))}
        </div>
      </div>
      {/* Categories Section - Let the page scroll naturally */}
      <div>
        {/* Scientific & Engineering Group */}
        <div className="py-4">
          <div className="px-6 mb-2">
            <span className={`text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-neutral-400' : 'text-gray-700'}`}>Scientific & Engineering</span>
          </div>
          {categories.filter(category => 
            ['power', 'voltage', 'energy', 'resistance', 'frequency'].includes(category.path)
          ).map((category) => (
            <CategorySection
              key={category.path}
              category={category}
              isSelected={selectedCategory === category.path}
              onSelect={onCategorySelect}
              selectedUnits={selectedUnits}
              onUnitSelect={onUnitSelect}
            />
          ))}
        </div>
        {/* Common Conversions Group */}
        <div className="py-4">
          <div className="px-6 mb-2">
            <span className={`text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-neutral-400' : 'text-gray-700'}`}>Common Conversions</span>
          </div>
          {categories.filter(category => 
            ['length', 'weight', 'temperature'].includes(category.path)
          ).map((category) => (
            <CategorySection
              key={category.path}
              category={category}
              isSelected={selectedCategory === category.path}
              onSelect={onCategorySelect}
              selectedUnits={selectedUnits}
              onUnitSelect={onUnitSelect}
            />
          ))}
        </div>
        {/* Digital & Computing Group */}
        <div className="py-4">
          <div className="px-6 mb-2">
            <span className={`text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-neutral-400' : 'text-gray-700'}`}>Digital & Computing</span>
          </div>
          {categories.filter(category => 
            ['number', 'color', 'image'].includes(category.path)
          ).map((category) => (
            <CategorySection
              key={category.path}
              category={category}
              isSelected={selectedCategory === category.path}
              onSelect={onCategorySelect}
              selectedUnits={selectedUnits}
              onUnitSelect={onUnitSelect}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const UnifiedUnitConverter = () => {
  
  // Initialize with number conversion and decimal to base64
  const [selectedCategory, setSelectedCategory] = useState('number');
  const [selectedUnits, setSelectedUnits] = useState({ 
    from: 'Decimal', 
    to: 'Base-64' 
  });

  // Add initial state for example visibility
  const [showInitialExample, setShowInitialExample] = useState(true);

  useEffect(() => {
    // Show initial example and explanation on first load
    if (showInitialExample) {
      // You could also store this in localStorage to only show on first visit
      setShowInitialExample(false);
    }
  }, []);

  const [recentConversions, setRecentConversions] = useState(() => {
    const saved = localStorage.getItem('recentConversions');
    return saved ? JSON.parse(saved) : [];
  });
  const navigate = useNavigate();
  const location = useLocation();

  // Prepare categories with conversion pairs
  const categories = Object.entries(conversionData).map(([key, value]) => ({
    path: key,
    name: value.name,
    units: value.units,
    convert: value.convert,
    conversionPairs: generateConversionPairs(value.units)
  }));

  useEffect(() => {
    localStorage.setItem('recentConversions', JSON.stringify(recentConversions));
  }, [recentConversions]);

  const handleConversionComplete = (result) => {
    const newConversion = {
      id: Date.now(), // unique identifier
      category: result.category,
      from: result.fromUnit,
      to: result.toUnit,
      inputValue: result.inputValue,
      outputValue: result.result,
      timestamp: new Date().toISOString()
    };
    
    // Update state with new conversion
    setRecentConversions(prev => {
      const updated = [newConversion, ...prev.slice(0, 9)]; // Keep last 10 conversions
      // Save to localStorage
      localStorage.setItem('recentConversions', JSON.stringify(updated));
      return updated;
    });
  };

  const onUnitSelect = (pair) => {
    setSelectedUnits({ from: pair.from, to: pair.to });
  };

  const [selectedConversion, setSelectedConversion] = useState('number');
  const [fromUnit, setFromUnit] = useState('');
  const [toUnit, setToUnit] = useState('');
  const [inputValue, setInputValue] = useState('');
  const calculatorRef = useRef(null);

  // Add handler for repeat conversion
  const handleRepeatConversion = (conversion) => {
    // Update the selected conversion type
    setSelectedConversion(conversion.category);
    
    // Update the conversion units
    setFromUnit(conversion.from);
    setToUnit(conversion.to);
    
    // Set the input value
    setInputValue(conversion.inputValue);

    // Scroll to calculator if needed
    calculatorRef.current?.scrollIntoView({ behavior: 'smooth' });

    // Trigger the conversion
    if (calculatorRef.current) {
      calculatorRef.current.convert();
    }
  };

  const handleUnitSelect = ({ category, from, to }) => {
    // Update the calculator's state
    setSelectedConversion(category);
    setFromUnit(from);
    setToUnit(to);

    // Clear any previous input to prevent unwanted conversions
    setInputValue('');

    // Safely scroll to calculator if ref exists
    if (calculatorRef.current && calculatorRef.current.scrollIntoView) {
      calculatorRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }

    // Remove the automatic conversion trigger
    // if (calculatorRef.current && calculatorRef.current.convert) {
    //   calculatorRef.current.convert();
    // }
  };

  const { darkMode } = useDarkMode();

  // Extract category from URL path
  const pathParts = location.pathname.split('/').filter(Boolean);
  const urlCategory = pathParts[1] || 'number'; // Default to 'number' if no category in URL
  const currentCategory = conversionData[urlCategory] || conversionData[selectedCategory];
  const categoryInfo = conversionCategories[urlCategory] || conversionCategories[selectedCategory] || conversionCategories.number;
  
  // Get comprehensive SEO metadata
  const seoData = getUnitConversionSEO(urlCategory || selectedCategory);
  const baseUrl = 'https://iconvertify.com';
  const currentPath = location.pathname;
  const canonicalUrl = `${baseUrl}${currentPath}`;
  const categoryTitle = currentCategory?.name || categoryInfo?.title || 'Unit Conversion';
  
  // Use SEO-optimized metadata
  const pageTitle = seoData.title;
  const fullDescription = seoData.description;
  const keywords = seoData.keywords;
  
  // Generate structured data
  const faqStructuredData = generateFAQStructuredData(seoData.faq);
  const howToStructuredData = generateHowToStructuredData(urlCategory || selectedCategory, categoryTitle);

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={fullDescription} />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        
        {/* OpenGraph */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={fullDescription} />
        <meta property="og:image" content={`${baseUrl}/favicon-assets/android-chrome-512x512.png`} />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta property="og:image:alt" content={`${categoryTitle} - iconvertify`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="iconvertify" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={fullDescription} />
        <meta name="twitter:image" content={`${baseUrl}/favicon-assets/android-chrome-512x512.png`} />
        <meta name="twitter:image:alt" content={`${categoryTitle} - iconvertify`} />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": `${categoryTitle} - iconvertify`,
            "url": canonicalUrl,
            "applicationCategory": "UtilityApplication",
            "operatingSystem": "Web Browser",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "description": fullDescription,
            "featureList": [
              "Free unit conversion",
              "Step-by-step explanations",
              "Real-time calculations",
              "No registration required",
              "Multiple unit systems"
            ]
          })}
        </script>
        {/* Breadcrumb Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": `${baseUrl}/`
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Unit Conversions",
                "item": `${baseUrl}/unit-conversions`
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": categoryTitle,
                "item": canonicalUrl
              }
            ]
          })}
        </script>
        {/* FAQ Structured Data */}
        {faqStructuredData && (
          <script type="application/ld+json">
            {JSON.stringify(faqStructuredData)}
          </script>
        )}
        {/* HowTo Structured Data */}
        {howToStructuredData && (
          <script type="application/ld+json">
            {JSON.stringify(howToStructuredData)}
          </script>
        )}
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-gray-50/50 via-white to-gray-50/50 dark:from-gray-950 dark:via-gray-950 dark:to-gray-900">
      {/* Updated Header */}
      <header className={`border-b top-16 z-10 transition-colors duration-300
        ${darkMode ? 'border-gray-800 bg-gray-950/90 shadow-[0_2px_24px_-8px_rgba(16,185,129,0.10)]' : 'border-gray-200/80 bg-white/80 backdrop-blur-sm'}`}
      >
        <div className="max-w-8xl mx-auto">
          <div className="px-6 pt-8 pb-6 flex flex-col items-start justify-center relative overflow-visible">
            {/* Faint SVG grid accent, ultra-faint */}
            <svg
              className="absolute left-0 top-0 w-full h-full pointer-events-none z-0"
              width="100%" height="100%" viewBox="0 0 600 120" fill="none"
              style={{ opacity: darkMode ? 0.025 : 0.012, filter: 'blur(1.5px)' }}
              aria-hidden="true"
            >
              <defs>
                <linearGradient id="gridGradient" x1="0" y1="0" x2="600" y2="120" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#38bdf8" stopOpacity="0.12" />
                  <stop offset="1" stopColor="#6366f1" stopOpacity="0.08" />
                </linearGradient>
              </defs>
              {/* Vertical lines */}
              {Array.from({ length: 13 }).map((_, i) => (
                <line key={i} x1={i * 50} y1="0" x2={i * 50} y2="120" stroke="url(#gridGradient)" strokeWidth="1" />
              ))}
              {/* Horizontal lines */}
              {Array.from({ length: 7 }).map((_, i) => (
                <line key={i} x1="0" y1={i * 20} x2="600" y2={i * 20} stroke="url(#gridGradient)" strokeWidth="1" />
              ))}
            </svg>
            {/* Hybrid: original size, modern minimal text */}
            <div className="relative z-10 flex flex-col items-start w-full">
              <span className="text-sm md:text-base font-semibold tracking-wider uppercase text-gray-500 dark:text-gray-400 select-none" style={{fontFamily: 'Inter, sf-pro-text, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif', letterSpacing: '0.09em'}}>
                All-in-one unit conversion, reimagined
              </span>
                    </div>
          </div>
        </div>
      </header>
      <main className="max-w-8xl mx-auto px-6 py-8">
        <div className="flex gap-8">
          {/* Left Sidebar */}
          <aside className="w-[340px] flex-shrink-0">
            <Sidebar
              categories={categories}
              selectedCategory={selectedCategory}
              selectedUnits={selectedUnits}
              onCategorySelect={setSelectedCategory}
              onUnitSelect={handleUnitSelect}
            />
          </aside>
          {/* Main Content */}
          <div className="flex-1 max-w-4xl" ref={calculatorRef}>
            {/* Engineering Information Box */}
            <div className={`p-6 rounded-2xl shadow-lg backdrop-blur-sm transition-colors duration-300
              ${darkMode ? 'bg-gray-900/90 border border-gray-800/70 text-neutral-100 shadow-[0_4px_32px_-8px_rgba(16,185,129,0.10)]' : 'bg-white/80 border border-gray-200/50 text-gray-900 shadow-gray-200/10'}`}
            >
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <h2 className={`text-2xl font-semibold bg-clip-text text-transparent 
                    bg-gradient-to-r ${darkMode ? 'from-neutral-100 via-neutral-300 to-neutral-400' : 'from-gray-900 via-gray-800 to-gray-700'}`}
                  >
                    Precision Engineering for Unit Transformations
                  </h2>
                  <p className={`max-w-2xl leading-relaxed ${darkMode ? 'text-neutral-300' : 'text-gray-600'}`}
                  >
                    Experience seamless unit conversions powered by advanced algorithms. From scientific calculations to 
                    everyday measurements, achieve precise results with comprehensive step-by-step explanations.
                  </p>
                </div>
                <div className={`flex items-center gap-1.5 text-sm ${darkMode ? 'text-emerald-400' : 'text-green-600'}`}>
                  <FiCheck className="w-4 h-4" />
                  Real-time<br />Processing
                </div>
              </div>
            </div>
            <div className="bg-white/90 dark:bg-gray-900/90 
                backdrop-blur-xl rounded-2xl 
                border border-gray-200/50 dark:border-gray-700/50
                shadow-xl shadow-gray-200/20 dark:shadow-gray-900/30
                overflow-hidden">
              <UnitConversionCalculator
                ref={calculatorRef}
                initialSelectedConversion={selectedConversion}
                initialFromUnit={fromUnit}
                initialToUnit={toUnit}
                initialInputValue={inputValue}
                onConversionComplete={handleConversionComplete}
              />
            </div>
          </div>
          {/* Right Sidebar */}
          <aside className="w-[280px] flex-shrink-0 hidden xl:block">
            <div className="sticky top-[84px]">
              <div className={`rounded-xl p-5 shadow-sm transition-colors duration-300
                ${darkMode ? 'bg-gray-950/95 border border-gray-800 text-neutral-100 shadow-[0_4px_32px_-8px_rgba(16,185,129,0.10)]' : 'bg-white border border-gray-200/80 text-gray-900'}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-[14px] font-medium tracking-tight flex items-center gap-2
                    ${darkMode ? 'text-neutral-100' : 'text-gray-900'}`}
                  >
                    <FiClock className={`w-4 h-4 ${darkMode ? 'text-primary-400' : 'text-gray-400'}`} />
                    Recent Conversions
                  </h3>
                  <button 
                    className={`text-xs transition-colors
                      ${darkMode ? 'text-neutral-400 hover:text-primary-400' : 'text-gray-400 hover:text-gray-600'}`}
                    onClick={() => setRecentConversions([])}
                  >
                    Clear All
                  </button>
                </div>
                <ConversionHistory 
                  recentConversions={recentConversions}
                  onRepeatConversion={handleRepeatConversion}
                />
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
    </>
  );
};

export default UnifiedUnitConverter;